/**
 * Number, money and time formatting. DESIGN.md section 7.5 of the UX spec.
 *
 * Rounding of a metric happens once, on the server, in the metric layer. Nothing here
 * recomputes a value; it only decides how many digits reach the screen.
 */
import type { MetricBasis, MetricEnvelope, MetricUnit } from '@elutide/shared';

export const OPS_TZ = 'America/New_York';

/** How fast a number is expected to move. Set once per metric, never per screen. */
export type AgeClass = 'live' | 'snapshot' | 'model';
export type AgeState = 'fresh' | 'stale' | 'dead' | 'unknown';

const AGE_THRESHOLDS: Record<AgeClass, { stale: number; dead: number }> = {
  live: { stale: 60_000, dead: 300_000 },
  snapshot: { stale: 900_000, dead: 2_700_000 },
  model: { stale: 86_400_000, dead: 604_800_000 },
};

export function ageStateFor(
  asOf: string | null | undefined,
  klass: AgeClass,
  now: number = Date.now(),
): AgeState {
  if (!asOf) return 'unknown';
  const at = Date.parse(asOf);
  if (Number.isNaN(at)) return 'unknown';
  const age = now - at;
  const thresholds = AGE_THRESHOLDS[klass];
  if (age < thresholds.stale) return 'fresh';
  if (age < thresholds.dead) return 'stale';
  return 'dead';
}

/** The worst of a set. Drives the global chip in the top bar. */
export function worstAgeState(states: readonly AgeState[]): AgeState {
  if (states.includes('dead')) return 'dead';
  if (states.includes('stale')) return 'stale';
  if (states.includes('fresh')) return 'fresh';
  return 'unknown';
}

/** Which freshness class a basis implies, so a screen never has to decide. */
export function ageClassForBasis(basis: MetricBasis): AgeClass {
  switch (basis) {
    case 'woo_paid':
    case 'woo_created':
    case 'refund_date':
    case 'ledger':
      return 'live';
    case 'model':
    case 'estimate':
      return 'model';
    default:
      return 'snapshot';
  }
}

/** Fixed vocabulary. One badge per basis, no invented labels. */
export const BASIS_LABEL: Record<MetricBasis, string> = {
  woo_paid: 'WOO PAID',
  woo_created: 'WOO CREATED',
  refund_date: 'REFUND DATE',
  ledger: 'LEDGER',
  snapshot: 'SNAP',
  estimate: 'EST',
  model: 'MODEL',
  manual: 'MANUAL',
  qbo: 'QBO',
};

export const BASIS_DEFINITION: Record<MetricBasis, string> = {
  woo_paid: 'From WooCommerce, paid statuses only, windowed on date_paid.',
  woo_created: 'From WooCommerce, windowed on the date the order was placed.',
  refund_date: 'Refunds windowed on the date the refund was created.',
  ledger: 'From an append-only ops ledger, not a derived figure.',
  snapshot: 'From a scheduled pull of an external source.',
  estimate: 'At least one component is estimated rather than observed.',
  model: 'Projected from a model, with its assumptions listed on the page.',
  manual: 'Entered by hand and carries the name of whoever entered it.',
  qbo: 'Read from QuickBooks Online.',
};

const usd = (fractionDigits: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  });

const usdCents = usd(2);
const usdWhole = usd(0);
const integers = new Intl.NumberFormat('en-US', { maximumFractionDigits: 0 });

/**
 * Money. Under 1,000 dollars with cents; 1,000 and above without cents in tables and tiles,
 * with cents anywhere a figure will be reconciled (`showCents`). Null is never zero.
 */
export function formatMoney(
  centsValue: number | null | undefined,
  options: { showCents?: boolean; nullText?: string } = {},
): string {
  if (centsValue === null || centsValue === undefined || Number.isNaN(centsValue)) {
    return options.nullText ?? 'no data';
  }
  const dollars = centsValue / 100;
  const magnitude = Math.abs(dollars);
  const withCents = options.showCents === true || magnitude < 1000;
  return withCents ? usdCents.format(dollars) : usdWhole.format(dollars);
}

export function formatCount(value: number | null | undefined, nullText = 'no data'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return nullText;
  return integers.format(value);
}

/** Ratios arrive as 0..1 and render with one decimal, none above 100 percent. */
export function formatPercent(ratio: number | null | undefined, nullText = 'no data'): string {
  if (ratio === null || ratio === undefined || Number.isNaN(ratio)) return nullText;
  const pct = ratio * 100;
  const digits = Math.abs(pct) >= 100 ? 0 : 1;
  return `${pct.toFixed(digits)}%`;
}

export function formatVelocity(value: number | null | undefined, nullText = 'no data'): string {
  if (value === null || value === undefined || Number.isNaN(value)) return nullText;
  return value.toFixed(1);
}

/** Days left. Zero velocity is words, never infinity and never a number. */
export function formatDaysLeft(days: number | null | undefined): string {
  if (days === null || days === undefined || Number.isNaN(days)) return 'no sales 30d';
  return `${Math.floor(days)}d`;
}

export function daysLeftTone(days: number | null | undefined): 'ok' | 'warn' | 'crit' | 'neutral' {
  if (days === null || days === undefined || Number.isNaN(days)) return 'neutral';
  if (days < 7) return 'crit';
  if (days <= 21) return 'warn';
  return 'ok';
}

/** Renders any envelope value with the right units. The one place a screen formats a metric. */
export function formatMetricValue(
  envelope: Pick<MetricEnvelope, 'value' | 'unit'> | null | undefined,
  options: { showCents?: boolean } = {},
): string {
  if (!envelope || envelope.value === null) return 'no data';
  return formatByUnit(envelope.value, envelope.unit, options);
}

export function formatByUnit(
  value: number,
  unit: MetricUnit,
  options: { showCents?: boolean } = {},
): string {
  switch (unit) {
    case 'cents':
      return formatMoney(value, options);
    case 'ratio':
      return formatPercent(value);
    case 'days':
      return `${Math.floor(value)}d`;
    case 'units_per_day':
      return `${value.toFixed(1)}/day`;
    case 'multiple':
      return `${value.toFixed(1)}x`;
    case 'units':
    case 'count':
    default:
      return formatCount(value);
  }
}

const MINUTE = 60_000;
const HOUR = 60 * MINUTE;
const DAY = 24 * HOUR;

/** Relative under 24 hours, then a short date. Never a bare timestamp in a cell. */
export function relativeAge(at: string | null | undefined, now: number = Date.now()): string {
  if (!at) return 'unknown';
  const then = Date.parse(at);
  if (Number.isNaN(then)) return 'unknown';
  const delta = Math.max(0, now - then);
  if (delta < MINUTE) return `${Math.floor(delta / 1000)}s`;
  if (delta < HOUR) return `${Math.floor(delta / MINUTE)}m`;
  if (delta < DAY) {
    const hours = Math.floor(delta / HOUR);
    const minutes = Math.floor((delta % HOUR) / MINUTE);
    return minutes > 0 && hours < 6 ? `${hours}h ${minutes}m` : `${hours}h`;
  }
  return shortDate(at);
}

const shortDateFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
  timeZone: OPS_TZ,
});

/** Same face, no timezone: for a calendar day that has already been resolved. */
const shortDateLocalFormat = new Intl.DateTimeFormat('en-US', {
  month: 'short',
  day: 'numeric',
});

const fullTimestampFormat = new Intl.DateTimeFormat('en-US', {
  dateStyle: 'medium',
  timeStyle: 'medium',
  timeZone: OPS_TZ,
});

/**
 * A YYYY-MM-DD that is already a New York date, formatted without moving it.
 *
 * `new Date('2026-03-04')` is parsed as UTC midnight, and formatting that in America/New_York
 * prints March 3. Every `NyDate` in the contract is a calendar day the business already
 * agreed on, so it must be read component-wise and never round tripped through an instant.
 * Anything that is not a plain date falls through to `shortDate`, which is the right reader
 * for a timestamp.
 */
export function shortNyDate(date: string | null | undefined): string {
  if (!date) return 'unknown';
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return shortDate(date);
  const [, year, month, day] = match;
  // Local noon, so no timezone on earth can push the formatter onto a neighbouring day.
  const parsed = new Date(Number(year), Number(month) - 1, Number(day), 12);
  if (Number.isNaN(parsed.valueOf())) return 'unknown';
  return shortDateLocalFormat.format(parsed);
}

export function shortDate(at: string | null | undefined): string {
  if (!at) return 'unknown';
  const parsed = new Date(at);
  if (Number.isNaN(parsed.valueOf())) return 'unknown';
  return shortDateFormat.format(parsed);
}

/** Full timestamp, in the store timezone. Shown on hover only. */
export function fullTimestamp(at: string | null | undefined): string {
  if (!at) return 'unknown';
  const parsed = new Date(at);
  if (Number.isNaN(parsed.valueOf())) return 'unknown';
  return `${fullTimestampFormat.format(parsed)} ${OPS_TZ}`;
}

/** Countdown for an auto-cancel or a cooldown. Negative is rendered as elapsed, not clamped. */
export function countdown(until: string | null | undefined, now: number = Date.now()): string {
  if (!until) return '';
  const target = Date.parse(until);
  if (Number.isNaN(target)) return '';
  const delta = target - now;
  const abs = Math.abs(delta);
  const text =
    abs < MINUTE
      ? `${Math.ceil(abs / 1000)}s`
      : abs < HOUR
        ? `${Math.ceil(abs / MINUTE)}m`
        : `${Math.floor(abs / HOUR)}h`;
  return delta >= 0 ? text : `${text} ago`;
}

/** Name plus last initial. The only customer shape allowed in a Slack line or a list. */
export function maskName(fullName: string): string {
  const parts = fullName.trim().split(/\s+/);
  const first = parts[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1] : '';
  return last ? `${first} ${last.charAt(0)}.` : first;
}
